import type { Src, TPlayer } from 'video.js'
import type { XhrRequest, XhrResponse } from '|>/shared/types/errors'

import videojs from 'video.js'
import { Events } from '|>/shared/events'
import { getVhs, getXhr } from '|>/shared/utils/vhs'
import { getResponseBytes, isHlsOrDashUrl } from './lib'

//
// Video.js plugins guide: https://videojs.com/guides/plugins/
//

// base plugin constructor
const BasePlugin: Src.Plugin = videojs.getPlugin('plugin') as Src.Plugin

/**
 * StreamStats plugin
 */
export class StreamStatsPlugin extends BasePlugin {
  declare player: TPlayer

  manifest?: string
  manifestUrl?: string
  manifestLatency?: number

  constructor(player: TPlayer) {
    super(player)

    // add xhr hook and event listeners
    player.on('xhr-hooks-ready', this.enable)
    player.on('loadedmetadata', this.emitManifestData)
    player.on([Events.Media.Load, 'playerreset', 'dispose'], this.reset)
  }

  enable = () => {
    getXhr(this.player, 'StreamStats plugin')?.onResponse(this.xhrResponseHook)
  }

  disable = () => {
    getXhr(this.player, 'StreamStats plugin')?.offResponse(this.xhrResponseHook)
  }

  reset = () => {
    this.manifest = undefined
    this.manifestUrl = undefined
    this.manifestLatency = undefined
  }

  emitManifestData = () => {
    if (this.manifest) {
      this.player.trigger(Events.Media.MasterManifestDownloaded, {
        manifest: this.manifest,
        latency: this.manifestLatency,
        url: this.manifestUrl,
      })
    }
  }

  emitBytesData = (bytes: number, bandwidth: number) => {
    this.player.trigger(Events.Media.TransmittedBytesInfo, {
      bytes,
      bandwidth,
    })
  }

  xhrResponseHook = (
    request: XhrRequest,
    _error: unknown,
    response: XhrResponse
  ) => {
    const url = request.url || request.uri || request.responseURL
    const type = request.requestType

    // skip unknown empty URLs
    if (!url) return

    // if the manifest is not yet set, and this response is likely a manifest, save it for later emission
    if (
      this.manifest == null &&
      (type === 'dash-manifest' ||
        type === 'hls-playlist' ||
        isHlsOrDashUrl(url)) &&
      typeof response.body === 'string'
    ) {
      this.manifest = response.body
      this.manifestUrl = url

      // `request` object is populated on the next tick
      Promise.resolve().then(() => {
        this.manifestLatency = request.roundTripTime
      })
    }

    // `request` object is populated on the next tick
    Promise.resolve().then(() => {
      const bytes = request.bytesReceived || getResponseBytes(response)
      const vhs = getVhs(this.player, 'StreamStats plugin')
      this.emitBytesData(bytes, vhs?.bandwidth || vhs?.systemBandwidth || 0)
    })
  }

  override dispose() {
    // remove xhr hook
    this.disable()

    // remove listeners
    const player = this.player
    player.off('xhr-hooks-ready', this.enable)
    player.off('loadedmetadata', this.emitManifestData)
    player.off([Events.Media.Load, 'playerreset', 'dispose'], this.reset)

    super.dispose()
  }
}

// register stream-stats plugin
videojs.registerPlugin('streamStats', StreamStatsPlugin)
